import React, {useState, useRef, useEffect, useCallback} from 'react';
import {setSelectedPulledElements, setBulkWindowPositions, setWindowPosition} from 'actions/index.js';
import {placeElement} from 'components/pulled/overlaps.js';
import {NW, NE, SW, SE} from 'components/windows/resize-types.js';

export const useWindowManager = ({
    sizeRef,
    sequencerEngine,
    scale,
    expand,
    ref,
    containerRef,
    size,
    setSize,
    element,
    elements,
    isPad,
    ordering,
    setOrdering,
    windowPosition
}) => {

    let [isDragging, setIsDragging] = useState(false);
    let [coords, setCoords] = useState(undefined);
    let startingPositions = useRef(undefined);
    let startingPoint = useRef(undefined);
    let [offset, setOffset] = useState({x: 0, y: 0});
    let [isResizing, setIsResizing] = useState(undefined);
    let resizePosition = useRef({x:0, y:0});
    let initialSize = useRef({width: 0, height: 0});
    let [windowScale, setWindowScale] = useState({x: 0, y:0});

    useEffect(() => {
        if (windowPosition) {
            adjust(windowPosition.x, windowPosition.y);
            element.x = windowPosition.x;
            element.y = windowPosition.y;
        }
    }, [windowPosition, setSize]);

    // called once at the beginning
    // places the element based on the x/y stored
    // inside it
    useEffect(() => {
        if (!containerRef) {
            return;
        }
        if (element && element.x !== undefined) {
            setCoords({x: element.x, y: element.y});
            if (element.width !== undefined) {
                setWindowScale({
                    x: element.width,
                    y: element.height
                });
            }
            adjust(element.x, element.y, true);
        }
        if (element && element.x === undefined) {
            placeWindow(element);
        }
        if (element && elements.current) {
            elements.current[element.id] = ref;
        }

        if (element.needsPlacement) {
            let {x,y,id} = element;
            let newCoords = placeElement(
                x,y,id,ref.current,
                elements.current);
            sequencerEngine.dispatch(setBulkWindowPositions(newCoords));
            
        }
    }, [element, setSize, setWindowScale]);

    const isInitialScale = (scale) => scale.x === 0 && scale.y === 0;

    const getSelected = () => {
        if (sequencerEngine) {
            let x = sequencerEngine.state.windows.selectedElements;
            return Object.keys(x);
        }
        return [];
    };

    useEffect(() => {
        if (sequencerEngine && coords) {
            sequencerEngine.dispatch(
                setWindowPosition(element.id, coords));
        }
    }, [coords]);

    const startResizing = useCallback((type, e) => {
        e.stopPropagation();
        initialSize.current = getWindowSize();
        resizePosition.current = getXY(e);
        setIsResizing(type);
        updateOrdering();
    }, [windowScale, setIsResizing]);

    const getWindowSize = useCallback(() => {
        return {
            width: ref.current.offsetWidth,
            height: ref.current.offsetHeight
        };
    }, []);

    useEffect(() => {
        if (element && element.x !== undefined) {
            setTimeout(() => {
                adjust(element.x, element.y);
            }, 50);
        }
    }, [expand]);

    useEffect(() => {
        // whenever the coords changes set this element to the top of the elements list
   }, [coords, ordering, setOrdering]);

    const placeWindow = useCallback((element) => {
        if (!containerRef) {
            return;
        }
        let positions = getPositions(elements);
        let {tops, lefts, widths, heights} = positions;
        for (let i in tops) {
            let top = tops[i];
            let left = lefts[i];
            let width = widths[i];
            let height = heights[i];
            let x = left+ 5;
            let y = top - height ;

            if (x + ref.current.offsetWidth <= containerRef.current.offsetWidth &&
                !doesElementIntersect(
                x,
                y,
                ref.current.offsetWidth,
                ref.current.offsetHeight,
                positions)) {

                setCoords({x,y});
                adjust(x,y,true);
                return;
            } else {
                x = left - width;
                y = top + 5;
                if (!doesElementIntersect(
                    x,
                    y,
                    ref.current.offsetWidth,
                    ref.current.offsetHeight,
                    positions)) {
                    
                    setCoords({x,y});
                    adjust(x,y,true);
                    return;
                }
            }
        }
    }, []);

    const adjust = useCallback((x,y, skipRest) => {
        if (!sizeRef || !ref.current) {
            return;
        }

        let size = sizeRef.current;
        if (size) {
            if (x + ref.current.offsetWidth > size.width ||
                y + ref.current.offsetHeight > size.height) {
                let _size = {
                    width: Math.max(size.width, x + ref.current.offsetWidth),
                    height: Math.max(size.height, y + ref.current.offsetHeight)
                };
                sizeRef.current = _size;
                setSize(_size);
                return;
            }

            if (skipRest) {
                return;
            }
            let {maxTop, maxLeft} = getMaxLeftAndTop(elements);
            if (maxLeft < size.width ||
                maxTop < size.height) {
                let _size = {
                    width: maxLeft,
                    height: maxTop,
                };
                sizeRef.current = _size;
                setSize(_size);
            }
        }
    }, [setSize, elements]);

    useEffect(() => {
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mousedown", onWindowDown);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mousedown", onWindowDown);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [
        setOffset,
        setCoords,
        isDragging,
        offset,
        setSize,
        isResizing,
        isDragging,
        setIsResizing,
        windowScale,
        setWindowScale]);

    const onWindowDown = useCallback((e) => {
    }, []);

    const onMouseDown = useCallback((e) => {
        e.stopPropagation();
        let rect = ref.current.getBoundingClientRect();

        startingPoint.current = {x: e.pageX, y: e.pageY};

        if (sequencerEngine) {
            let pts = {};
            for (let id in elements.current) {
                let pos = sequencerEngine.state.windows.windowPositions[id];
                pts[id] = {... pos};
            }
            startingPositions.current = pts;
        }
 
        if (sequencerEngine) {
            if (!getSelected().includes(element.id)) {
                sequencerEngine.dispatch(
                    setSelectedPulledElements({}));
            }
        }
        setOffset({x: e.clientX - rect.left, y: e.clientY - rect.top});
        setIsDragging(true);
        updateOrdering();
    }, [setOffset, setIsDragging, ordering, setOrdering]);

    const onContextMenu = useCallback((e) => {
        e.stopPropagation();
        updateOrdering();
    }, [ordering, setOrdering]);

    const onMouseUp = useCallback(() => {
        if (isDragging || isResizing) {
            setIsResizing(false);
            setIsDragging(false);
            setOffset({x: 0, y: 0});
        }
    }, [setIsDragging, setIsResizing, setOffset, isResizing, isDragging]);

    const getXY = useCallback((e,skipScale) => {
        let x = e.pageX;
        let y = e.pageY;
        if (containerRef) {
            let rect = containerRef.current.getBoundingClientRect();
            x = Math.max(0, e.pageX - rect.left - offset.x);
            y = Math.max(0, e.pageY - rect.top - offset.y);
            if (!skipScale) {
                x/=scale;
                y/=scale;
            }
        }
        return {x,y};
    }, [containerRef, offset]);

    const onMouseMove = useCallback((e) => {

        if (!isDragging && !isResizing) { return; }
        let {x,y} = getXY(e);
        if (!containerRef) {
           setCoords({x: e.pageX - offset.x, y: e.pageY - offset.y});
           return;
        }

        if (isDragging) {
            
            let selected = getSelected();
            let xy = {x: e.pageX, y: e.pageY};
            if (selected.length > 0 && xy && startingPoint.current) {
                let xDiff = xy.x - startingPoint.current.x;
                let yDiff = xy.y - startingPoint.current.y;
                let positions = {};
                for (let id of selected) {
                    if (id === element.id) {
                        continue;
                    }
                    // otherwise
                    let position = startingPositions.current[id];
                    positions[id] = {
                        x: Math.max(0, position.x + (xDiff)/scale),
                        y: Math.max(0, position.y + (yDiff)/scale),
                    };
                }
                sequencerEngine.dispatch(setBulkWindowPositions(positions));
            }
            adjust(x,y);
            setCoords({x,y});

            element.x = x;
            element.y = y;
        } else if (isResizing) {
            resizeWindow(isResizing, x, y, element);
        }
 
    }, [isDragging, setOffset, setCoords, offset, setSize, elements, scale, setWindowScale, windowScale, isResizing]);

    const updateOrdering = useCallback(() => {
        if (!ordering) {
            return;
        }
        if (ordering.length > 0 && ordering.indexOf(element) === ordering.length - 1) {
            return;
        }
        let _ordering = ordering.filter(x => x !== element);
        _ordering = [... _ordering, element];
        setOrdering(_ordering);
    }, [ordering, setOrdering, element]);
 

    const resizeWindow = useCallback((direction, x, y, element) => {
        let xOffset = x - resizePosition.current.x;
        let yOffset = y - resizePosition.current.y;
        let {width, height} = initialSize.current;
        if (direction === NW) {
            setCoords({x,y});
        } else if (direction === SW) {
            y = element.y;
            yOffset *= -1;
        } else if (direction === NE) {
            xOffset *= -1;
            x = element.x;
        } else if (direction === SE) {
            xOffset *= -1;
            yOffset *= -1;
            x = undefined;
            y = undefined;
        }

        let newWidth = width - xOffset;
        let newHeight = height - yOffset;

        element.height = newHeight;
        element.width = newWidth;
        setWindowScale({x: newWidth, y: newHeight});

        adjust(element.x, element.y);

        if (x !== undefined && y !== undefined) {
            setCoords({x,y});
        }
    }, [windowScale, setWindowScale, setCoords]);
    
    let style = {};
    if (coords) {
        style = {
            top: coords.y,
            left: coords.x
        };
    };
    if (ordering && ordering.includes(element)) {
        style.zIndex = 1000000 * (1 + ordering.indexOf(element));
    }
    return {
        adjust,
        onContextMenu,
        setWindowScale,
        style,
        isDragging,
        onMouseDown,
        windowScale,
        onMouseUp,
        coords,
        setCoords,
        isDragging,
        startResizing
    };
};

const getPositions = (elements) => {
    let lefts = [];
    let tops = [];
    let widths = [];
    let heights = [];
    for (let id in elements.current) {
        let _element = elements.current[id].current;
        if (!_element) {
            continue;
        }
        lefts.push(_element.offsetLeft + _element.offsetWidth);
        tops.push(_element.offsetTop + _element.offsetHeight);
        heights.push(_element.offsetHeight);
        widths.push(_element.offsetWidth);
    }
    return {lefts,tops, widths, heights};
};

const doesElementIntersect = (x, y, width, height, positions) => {
    let {lefts, tops, widths, heights} = positions;
    for (let i in lefts) {
        let _width = widths[i];
        let _height = heights[i];
        let _left = lefts[i] - _width;
        let _top = tops[i] - _height;
        if (x >= _left && x <= _left + _width &&
            y >= _top && y <= _top + _height) {
            return true;
        }
    }
    return false;
};


const getMaxLeftAndTop= (elements) => {
    let {lefts,tops} = getPositions(elements);
    let maxLeft = Math.max(... lefts);
    let maxTop = Math.max(... tops);
    return {maxLeft, maxTop};
};
